import React, { createContext, useState, useEffect, useContext, ReactNode } from "react";
import { isTokenExpired } from "./AuthTokenChecker";
import { jwtDecode } from "jwt-decode";

type AuthContextType = {
  user: any | null; // User data decoded from token
  token: string | null; // Raw authentication token
  loading: boolean; // Indicates if auth state is still being initialized
  userAccounts: AccountModel[]; //list of user accounts
  currentAccount: AccountModel | null; //current account
  switchToAccount: (account: AccountModel) => void;
  getAccount: (id: string) => AccountModel | undefined;
  login: (token: string) => void;
  logout: (account: AccountModel | null) => void;
};

export interface AccountModel {
  id: string;
  name: string;
  slug: string;
  logo: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  //todo:obviously make this dynamic
  const allAccountsId = new Map<string, AccountModel>([
    ["fieldbook-dev-a7tga", { id: "fieldbook-dev-a7tga", name: "Fieldbook Dev", slug: "dev", logo: "/Fieldbook.png" }],
    ["murphy-co-t5161", { id: "murphy-co-t5161", name: "Murphy Co.", slug: "murphyco", logo: "/Murphyco.png" }],
  ]);

  //todo: obviously this is terrible
  const allAccountsSlug = new Map<string, AccountModel>([
    ["dev", { id: "fieldbook-dev-a7tga", name: "Fieldbook Dev", slug: "dev", logo: "/Fieldbook.png" }],
    ["murphyco", { id: "murphy-co-t5161", name: "Murphy Co.", slug: "murphyco", logo: "/Murphyco.png" }],
  ]);

  const [user, setUser] = useState<any | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [currentAccount, setCurrentAccount] = useState<AccountModel | null>(null);
  const [userAccounts, setUserAccounts] = useState<AccountModel[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  // Load user/token from local storage on app initialization
  const initTokenFromStorage = () => {
    const storedToken = localStorage.getItem("auth_token");
    if (storedToken) {
      try {
        login(storedToken);
      } catch (err: any) {
        console.error("Could not login using stored token.");
        //restore account context from storage since it could not come from token
        initAccountFromStorage();
      }
    }
  };

  // Load account context from local storage (will be overwritten by token)
  const initAccountFromStorage = () => {
    const storedAccount: string | null = localStorage.getItem("account");
    if (storedAccount) {
      const account = allAccountsId.get(storedAccount);
      if (account) {
        setCurrentAccount(account);
      }
    }
  };

  // Load user accounts from local storage on app init
  const initUserAccountsFromStorage = () => {
    const accountData = localStorage.getItem("user_accounts");
    if (accountData) {
      const ids: string[] = JSON.parse(accountData); //user accounts
      const accounts: AccountModel[] = [];
      for (const id of ids) {
        const m = allAccountsId.get(id);
        if (m) accounts.push(m);
      }
      setUserAccounts(accounts);
    }
  };

  useEffect(() => {
    setLoading(true);
    initTokenFromStorage();
    initUserAccountsFromStorage();
    setLoading(false);
  }, []);

  /*
  const generateMockToken = () => {
    const header = { alg: "HS256", typ: "JWT" };
    const payload = {
      sub: "1234567890",
      name: "Test User",
      iat: Math.floor(Date.now() / 1000),
      exp: Math.floor(Date.now() / 1000) + 10, // Expires in 10 seconds
    };

    const base64Encode = (obj: any) => {
      return btoa(unescape(encodeURIComponent(JSON.stringify(obj))));
    };

    return `${base64Encode(header)}.${base64Encode(payload)}.dummy-signature`;
  };*/

  const getAccount = (slug: string) => {
    return allAccountsSlug.get(slug);
  };

  const switchToAccount = (account: AccountModel) => {
    console.log(`Switching to account ${account.id}.`);
    //only to set context. this leaves accout token intact, since user may switch back.
    localStorage.setItem("account", account.id);
    setCurrentAccount(account);

    //reset the current user/tokens and attempt to log in using a stored account token
    const accountToken = localStorage.getItem(account.id);
    if (!accountToken) {
      setUser(null);
      setToken(null);
      throw new Error(`No token found for account ${account.id}`);
    } else {
      login(accountToken); //may throw an error, but let caller handle it
    }
  };

  //listen for changes to current account and update user's local list
  useEffect(() => {
    if (currentAccount) {
      if (!userAccounts?.find((a) => a.id == currentAccount.id)) {
        const ac = [...userAccounts, currentAccount];
        const ids: string[] = ac.map((a) => a.id);
        localStorage.setItem("user_accounts", JSON.stringify(ids));
        setUserAccounts(ac);
      }
    }
  }, [currentAccount]);

  const login = (newToken: string) => {
    if (!newToken) throw new Error("An auth token was not provided.");

    //if the local token has expried, login is aborted and user is prompted
    if (isTokenExpired(newToken)) throw new Error("Auth token has expired.");

    try {
      const decodedUser: any = jwtDecode(newToken);
      //console.log("decoded user", decodedUser);
      const tenant = decodedUser.firebase.tenant;
      const account = allAccountsId.get(tenant);
      if (account) {
        setUser(decodedUser);
        setToken(newToken);
        localStorage.setItem("auth_token", newToken);

        //keep a copy of the account token handy for switching between accounts
        localStorage.setItem(tenant, newToken);

        //automatically switch over to the current account
        setCurrentAccount(account);
        localStorage.setItem("account", tenant);
      } else {
        throw new Error(`No account found for tenant '${tenant}'.`);
      }
    } catch (error: any) {
      console.error(error);
      throw new Error(`Error decoding login token: "${error.message}"`);
    }
  };

  const logout = (account: AccountModel | null) => {
    if (account) {
      //remove the account's auth token, but keeps current account as is
      localStorage.removeItem(account.id);
    }

    //clear out the current user/token, forcing login for current account
    setUser(null);
    setToken(null);
    localStorage.removeItem("auth_token");

    console.log("User logged out.");
  };

  return (
    <AuthContext.Provider
      value={{ user, token, loading, userAccounts, currentAccount, login, logout, switchToAccount, getAccount }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Hook to use the AuthContext in components
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
